import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  AlertTriangle,
  Shield,
  Zap,
  Battery,
  Thermometer,
  Cable,
  ChevronDown,
  ChevronUp,
  MapPin,
  ListChecks
} from "lucide-react";

const SafetyInfo = () => {
  const [safetyInfo, setSafetyInfo] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    fetchSafetyInfo();
  }, []);

  const fetchSafetyInfo = async () => {
    try {
      const response = await axios.get(`${API}/safety/info`);
      setSafetyInfo(response.data);
      // Expand critical items by default
      const initialExpanded = {};
      response.data.forEach((item, index) => {
        if (item.severity === "critical") {
          initialExpanded[index] = true;
        }
      });
      setExpandedItems(initialExpanded);
    } catch (error) {
      console.error("Failed to fetch safety info:", error);
    }
  };

  const toggleExpand = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical":
        return <AlertTriangle className="h-6 w-6 text-[#FF3B30]" />;
      case "warning":
        return <Shield className="h-6 w-6 text-[#FF9F0A]" />;
      default:
        return <Shield className="h-6 w-6 text-[#007AFF]" />;
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case "critical":
        return "bg-[#FF3B30]/10 border-[#FF3B30]/30";
      case "warning":
        return "bg-[#FF9F0A]/10 border-[#FF9F0A]/30";
      default:
        return "bg-[#007AFF]/10 border-[#007AFF]/30";
    }
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="header-bar">
        <div>
          <h1 className="header-title">Safety Information</h1>
          <p className="header-subtitle">Critical safety procedures and warnings for 2013 Tesla Model S</p>
        </div>
      </div>

      {/* Critical Banner */}
      <div className="mb-6 p-6 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded service-mode">
        <div className="flex items-start gap-4">
          <Zap className="h-8 w-8 text-[#FF3B30]" />
          <div>
            <p className="text-[#FF3B30] font-heading text-xl uppercase tracking-wider">High Voltage Warning</p>
            <p className="text-white/80 mt-2">
              The 2013 Tesla Model S operates on a <span className="font-bold text-[#FF3B30]">400V DC high voltage system</span>. 
              Direct contact with HV components can cause <span className="font-bold">severe injury or death</span>.
            </p>
            <p className="text-white/60 mt-2 text-sm">
              Always follow proper safety procedures. When in doubt, contact a certified Tesla technician.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Reference Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="glass-card p-4">
          <div className="flex items-center gap-3 mb-3">
            <Battery className="h-5 w-5 text-[#FF9F0A]" />
            <span className="font-heading uppercase tracking-wider text-white">12V Battery</span>
          </div>
          <p className="text-white/60 text-sm">33Ah AGM battery located in the <span className="text-[#007AFF]">front trunk (frunk)</span>, driver side.</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-3 mb-3">
            <Zap className="h-5 w-5 text-[#FF3B30]" />
            <span className="font-heading uppercase tracking-wider text-white">Pyro Fuse</span>
          </div>
          <p className="text-white/60 text-sm">Between car floor and main battery top. <span className="text-[#FF3B30]">Extremely difficult to access</span> on 2012-2013 models.</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-3 mb-3">
            <Cable className="h-5 w-5 text-[#FF9F0A]" />
            <span className="font-heading uppercase tracking-wider text-white">HV Cables</span>
          </div>
          <p className="text-white/60 text-sm"><span className="text-[#FF9F0A]">Orange cables = HIGH VOLTAGE</span>. Never cut or tamper with orange cables.</p>
        </div>
      </div>

      {/* Detailed Safety Information */}
      <div className="space-y-4">
        {safetyInfo.map((item, index) => (
          <div 
            key={index}
            className={`border rounded-lg overflow-hidden ${getSeverityClass(item.severity)}`}
            data-testid={`safety-item-${index}`}
          >
            <button
              onClick={() => toggleExpand(index)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid={`safety-toggle-${index}`}
            >
              <div className="flex items-center gap-4">
                {getSeverityIcon(item.severity)}
                <div className="text-left">
                  <h3 className="font-heading text-lg uppercase tracking-wider text-white">{item.title}</h3>
                  <p className="text-white/50 text-sm mt-1">{item.location}</p>
                </div>
              </div>
              {expandedItems[index] ? (
                <ChevronUp className="h-5 w-5 text-white/50" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white/50" />
              )}
            </button>
            
            {expandedItems[index] && (
              <div className="px-4 pb-4 slide-up">
                <div className="ml-10 space-y-4">
                  {/* Description */}
                  <div>
                    <p className="text-white/80">{item.description}</p>
                  </div>
                  
                  {/* Location */}
                  {item.location && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-[#007AFF] mt-0.5" />
                      <div>
                        <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Location</p>
                        <p className="text-white/80">{item.location}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Procedures */}
                  {item.procedures && item.procedures.length > 0 && (
                    <div className="flex items-start gap-3">
                      <ListChecks className="h-5 w-5 text-[#30D158] mt-0.5" />
                      <div>
                        <p className="text-xs text-white/50 uppercase tracking-wider mb-2">Safety Procedures</p>
                        <ul className="space-y-2">
                          {item.procedures.map((procedure, pIndex) => (
                            <li key={pIndex} className="flex items-start gap-2 text-white/70 text-sm">
                              <span className="text-[#007AFF] font-mono">{pIndex + 1}.</span>
                              <span>{procedure}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Emergency Contact */}
      <div className="mt-8 p-6 glass-card">
        <h3 className="font-heading text-lg uppercase tracking-wider text-white mb-4">Emergency Procedures</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-white/50 text-sm uppercase tracking-wider mb-2">In Case of Fire</p>
            <ul className="text-white/80 text-sm space-y-1">
              <li>• Call 911 immediately</li>
              <li>• Evacuate all occupants</li>
              <li>• Do NOT use water on lithium battery fires</li>
              <li>• Inform firefighters of high-voltage EV</li>
              <li>• Keep a safe distance (battery may reignite)</li>
            </ul>
          </div>
          <div>
            <p className="text-white/50 text-sm uppercase tracking-wider mb-2">In Case of Collision</p>
            <ul className="text-white/80 text-sm space-y-1">
              <li>• Turn off vehicle if possible</li>
              <li>• Do NOT touch any orange cables</li>
              <li>• Do NOT attempt to cut vehicle for extraction</li>
              <li>• Wait for trained emergency responders</li>
              <li>• Inform responders of high-voltage system</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 p-4 border border-white/10 rounded text-center">
        <p className="text-white/40 text-xs">
          This information is provided for educational purposes. Always refer to official Tesla service documentation 
          and consult with certified technicians for any service procedures. Working on high-voltage systems requires 
          proper training, equipment, and certification.
        </p>
      </div>
    </div>
  );
};

export default SafetyInfo;
